import React, { useRef, useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { GoNumber, GoTriangleLeft } from "react-icons/go";
import { FaChartBar, FaChartLine, FaDownload } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";
import { color } from "echarts";

const LEVEL_COLORS = {
  months: ["#06B6D4"],
  lobby: ["#14B8A6"],
  buckets: ["#2DD4BF", "#14B8A6", "#06B6D4", "#5EEAD4", "#0D9488"],
};

const getLevelType = (optionId) => {
  if (optionId === "months") return "months";
  if (optionId.includes("-")) return "buckets";
  return "lobby";
};

const NewMultiLevelBarChart = ({ allLevelData, rootId }) => {
  const chartRef = useRef(null);
  const optionStack = useRef([]);
  const allOptions = useRef({});
  const [ready, setReady] = useState(false);
  const [currentOptionId, setCurrentOptionId] = useState(null);
  const [breadcrumbIds, setBreadcrumbIds] = useState([]);
  const [chartType, setChartType] = useState("bar");
  const [showValues, setShowValues] = useState(false);

  useEffect(() => {
    if (!ready) return;

    const startId = rootId || allLevelData[0][0][2];

    setCurrentOptionId(startId);
    setBreadcrumbIds([startId]);
    optionStack.current = [];
  }, [ready, rootId, allLevelData]);

  useEffect(() => {
    if (!allLevelData?.length) return;

    const optionsMap = {};

    allLevelData.forEach((data) => {
      const optionId = data[0][2];
      const level = getLevelType(optionId);

      optionsMap[optionId] = {
        id: optionId,
        color: LEVEL_COLORS[level],
        animationDurationUpdate: 500,
        xAxis: {
          type: "category",
          axisLabel: {
            interval: 0,
            fontSize: 11.5,
            rotate: 45,
            color: "rgba(73, 73, 73, 1)",
            // fontWeight: "bold",
          },
        },
        yAxis: {
          minInterval: 0,
          color: "rgba(73, 73, 73, 1)",
        },
        series: [
          {
            type: "bar",
            dimensions: ["x", "y", "groupId", "childGroupId"],
            encode: {
              x: "x",
              y: "y",
              itemGroupId: "groupId",
              itemChildGroupId: "childGroupId",
            },
            data,
            label: {
              show: showValues,
              position: "top",
              color: "#333",
              fontWeight: "bold",
              formatter: (params) => params.value[1],
            },
            itemStyle: {
              borderRadius: [8, 8, 0, 0],
              color: (params) => {
                const level = getLevelType(optionId);
                const palette = LEVEL_COLORS[level] || [];
                return {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    {
                      offset: 0,
                      color: palette[params.dataIndex % palette.length],
                    },
                    { offset: 1, color: "#c4c4c4cb" },
                  ],
                };
              },
              shadowColor: "rgba(0, 0, 0, 0.47)",
              shadowBlur: 4,
            },
            universalTransition: {
              enabled: true,
              divideShape: "clone",
            },
          },
        ],
      };
    });

    allOptions.current = optionsMap;
    setReady(true);
  }, [allLevelData, showValues]);

  const goForward = (nextId) => {
    optionStack.current.push(currentOptionId);
    setCurrentOptionId(nextId);
    setBreadcrumbIds((prev) => [...prev, nextId]);
  };

  const goBack = () => {
    if (!optionStack.current.length) return;
    const prevId = optionStack.current.pop();
    setCurrentOptionId(prevId);
    setBreadcrumbIds((prev) => prev.slice(0, -1));
  };

  const onChartReady = (chart) => {
    chart.on("click", "series", (params) => {
      if (params.data?.[3]) {
        goForward(params.data[3]);
      }
    });
  };

  if (!ready || !currentOptionId || !allOptions.current[currentOptionId]) {
    return null;
  }

  const downloadChart = () => {
    const chart = chartRef.current.getEchartsInstance();
    const img = chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });
    const a = document.createElement("a");
    a.href = img;
    a.download = `chart.png`;
    a.click();
  };

  // --------- Button configs for DRY rendering ---------
  const buttons = [
    {
      icon: <GoNumber />,
      active: showValues,
      onClick: () => setShowValues((s) => !s),
      label: showValues ? "Hide Values" : "Values",
    },
    { icon: <FaDownload />, onClick: downloadChart },
    {
      icon: <FaChartBar />,
      active: chartType === "bar",
      onClick: () => setChartType("bar"),
    },
    {
      icon: <FaChartLine />,
      active: chartType === "line",
      onClick: () => setChartType("line"),
    },
    {
      icon: <RiResetLeftLine />,
      onClick: () => {
        setChartType("bar");
        setShowValues(false);
      },
    },
  ];

  return (
    <div>
      {/* BREADCRUMB */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            {breadcrumbIds.map((id, index) => (
              <li
                key={id}
                className={`breadcrumb-item ${
                  index === breadcrumbIds.length - 1 ? "active" : ""
                }`}
              >
                {id}
              </li>
            ))}
          </ol>
        </nav>

        {/* Right side: Back button + control buttons */}
        <div className="d-flex align-items-center ms-auto">
          {/* Back Button */}
          <button
            type="button"
            className="btn-sm btn btn-outline-dark d-flex align-items-center me-2"
            onClick={goBack}
            style={{ position: "relative", zIndex: 2 }}
          >
            <GoTriangleLeft
              className="me-1"
              style={{ pointerEvents: "none" }}
            />
            <span>Back</span>
          </button>

          {/* Control Buttons */}
          {buttons.map((btn, idx) => (
            <button
              key={idx}
              className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
                btn.active ? "active" : ""
              }`}
              style={{ height: "25px" }}
              onClick={btn.onClick}
            >
              {btn.icon}
              {btn.label && <span className="ms-1">{btn.label}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* CHART */}
      <ReactECharts
        key={currentOptionId}
        ref={chartRef}
        option={{
          ...allOptions.current[currentOptionId],
          series: allOptions.current[currentOptionId].series.map((s) => ({
            ...s,
            type: chartType,
            label: { ...s.label, show: showValues },
          })),
        }}
        onChartReady={onChartReady}
        style={{ height: "350px", width: "100%" }}
        notMerge
      />
    </div>
  );
};

export default NewMultiLevelBarChart;
